import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import type { ARTPMOC, ARTPMOCCreateInput } from '../types/pmocPlanos'
import type { PaginatedResponse } from '../types'

export function useArtsPmocList(pmocId: string) {
  return useQuery({
    queryKey: ['arts-pmoc', pmocId],
    queryFn: async () => {
      const r = await api.get<PaginatedResponse<ARTPMOC>>(`/pmoc/${pmocId}/arts/`)
      return r.data
    },
    enabled: !!pmocId,
  })
}

function montaFormData(input: ARTPMOCCreateInput): FormData {
  const fd = new FormData()
  Object.entries(input).forEach(([k, v]) => {
    if (v === undefined || v === null) return
    if (v instanceof File) {
      fd.append(k, v)
    } else if (typeof v === 'boolean') {
      fd.append(k, v ? 'true' : 'false')
    } else {
      fd.append(k, String(v))
    }
  })
  return fd
}

export function useArtPmocCreate(pmocId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: ARTPMOCCreateInput) => {
      const hasFile = input.arquivo instanceof File
      const config = hasFile
        ? { headers: { 'Content-Type': 'multipart/form-data' } }
        : undefined
      const body = hasFile ? montaFormData(input) : input
      const r = await api.post<ARTPMOC>(`/pmoc/${pmocId}/arts/`, body, config)
      return r.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['arts-pmoc', pmocId] })
      qc.invalidateQueries({ queryKey: ['pmoc', pmocId] })
    },
  })
}

export function useArtPmocUpdate(pmocId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: Partial<ARTPMOCCreateInput>
    }) => {
      const hasFile = data.arquivo instanceof File
      const config = hasFile
        ? { headers: { 'Content-Type': 'multipart/form-data' } }
        : undefined
      const body = hasFile
        ? montaFormData(data as ARTPMOCCreateInput)
        : data
      const r = await api.patch<ARTPMOC>(`/arts-pmoc/${id}/`, body, config)
      return r.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['arts-pmoc', pmocId] })
    },
  })
}

export function useArtPmocDelete(pmocId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/arts-pmoc/${id}/`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['arts-pmoc', pmocId] }),
  })
}
